import React, { useState } from 'react';
import { ArrowLeft, CreditCard, Users, Plus, Trash2, CloudUpload, X, CheckCircle } from 'lucide-react';
import { createAsaasClient } from '../services/asaasApi';
import './AsaasStep2.css';

// Constantes para dependentes
const emptyDep = { nome: '', dataNascimento: '', grau: 'filho' };
const grauLabels = { filho: 'Filho(a)', conjuge: 'Cônjuge', pai: 'Pai/Mãe', agregado: 'Agregado' };

export default function AsaasStep2({ onBack, onNavigate, onSaveAndCharge }) {
  // Dados do cliente
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cpfCnpj: '',
    phone: '',
    mobilePhone: '',
    address: '',
    addressNumber: '',
    complement: '',
    province: '',
    postalCode: '',
    city: '',
    state: '',
    plano: '',
    vendedor: ''
  });

  // Documentos anexados
  const [docs, setDocs] = useState([]);
  // Dependentes
  const [depForm, setDepForm] = useState(emptyDep);
  const [dependentes, setDependentes] = useState([]);

  // Estados de UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  // Documentos
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newDocs = files.map(file => ({
      name: file.name,
      file: file,
      url: URL.createObjectURL(file)
    }));
    setDocs(prev => [...prev, ...newDocs]);
  };

  const removeDoc = (index) => {
    setDocs(prev => prev.filter((_, i) => i !== index));
  };

  // Dependentes
  const setD = (k, v) => setDepForm(p => ({ ...p, [k]: v }));
  const addDependente = () => {
    if (!depForm.nome) return;
    setDependentes(p => [...p, { ...depForm, id: Date.now() }]);
    setDepForm(emptyDep);
  };
  const removeDep = (id) => setDependentes(p => p.filter(d => d.id !== id));
  const calcIdade = (dob) => {
    if (!dob) return '';
    const diff = Date.now() - new Date(dob).getTime();
    return `(${Math.floor(diff / (1000 * 60 * 60 * 24 * 365))})`;
  };

  // Função auxiliar para criar cliente no Asaas
  const criarClienteAsaas = async () => {
    if (!formData.name || !formData.cpfCnpj || !formData.email) {
      setError('Nome, CPF/CNPJ e e-mail são obrigatórios');
      return null;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        cpfCnpj: formData.cpfCnpj.replace(/\D/g, ''),
        phone: formData.phone.replace(/\D/g, ''),
        mobilePhone: formData.mobilePhone.replace(/\D/g, ''),
        address: formData.address,
        addressNumber: formData.addressNumber,
        complement: formData.complement,
        province: formData.province,
        postalCode: formData.postalCode.replace(/\D/g, ''),
        city: formData.city,
        state: formData.state
      };

      const result = await createAsaasClient(payload);
      if (result && result.id) {
        return {
          asaasId: result.id,
          name: formData.name,
          email: formData.email,
          cpfCnpj: formData.cpfCnpj,
          phone: formData.phone,
          mobilePhone: formData.mobilePhone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          plano: formData.plano,
          vendedor: formData.vendedor,
          docs,
          dependentes
        };
      } else {
        setError('Erro ao criar cliente no Asaas. Tente novamente.');
        return null;
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Erro ao conectar com o servidor');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Salvar apenas
  const handleSave = async () => {
    setSaving(true);
    const cliente = await criarClienteAsaas();
    setSaving(false);
    if (cliente) {
      setSavedOk(true);
      setTimeout(() => {
        setSavedOk(false);
        onBack();
      }, 1500);
    }
  };

  // Salvar e gerar cobrança
  const handleSaveAndCharge = async () => {
    setSaving(true);
    const cliente = await criarClienteAsaas();
    setSaving(false);
    if (cliente) {
      setSavedOk(true);
      setTimeout(() => {
        setSavedOk(false);
        if (onSaveAndCharge) {
          onSaveAndCharge(cliente);
        } else if (onNavigate) {
          onNavigate('gerar-cobranca', { client: cliente });
        }
      }, 1500);
    }
  };

  return (
    <div className="clients-view">
      {/* Overlay de salvando/salvo */}
      {(saving || savedOk) && (
        <div className="save-overlay">
          <div className="save-overlay-box">
            {saving && <><div className="save-spinner" /><p>Salvando cadastro...</p></>}
            {savedOk && <><div className="save-check">✓</div><p>Cadastro salvo!</p></>}
          </div>
        </div>
      )}

      <div className="step2-topbar">
        <div className="step2-topbar-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
          <div className="step-badge asaas-badge">
            <CreditCard size={15} /> Asaas
          </div>
          <h2 className="view-title" style={{ margin: 0 }}>Cadastro de Clientes — Dados do Cliente</h2>
          <p className="step-description" style={{ margin: 0 }}>
            Preencha os dados do cliente para cadastrá-lo na plataforma Asaas
          </p>
        </div>
        <button className="back-button" style={{ margin: 0, alignSelf: 'flex-start' }} onClick={onBack}>
          <ArrowLeft size={16} /> Voltar
        </button>
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        <div className="cpfl-form-card">
          {/* Linha 1: Nome, CPF, Email, Plano (4 colunas) */}
          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px' }}>
            <div className="s2-field" style={{ gridColumn: 'span 1' }}>
              <label>NOME COMPLETO *</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} required />
            </div>
            <div className="s2-field">
              <label>CPF / CNPJ *</label>
              <input type="text" name="cpfCnpj" value={formData.cpfCnpj} onChange={handleChange} required />
            </div>
            <div className="s2-field">
              <label>E-MAIL *</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>
            <div className="s2-field">
              <label>PLANO</label>
              <select name="plano" value={formData.plano} onChange={handleChange}>
                <option value="">Selecione</option>
                <option value="Humano">Humano</option>
                <option value="Pet">Pet</option>
                <option value="Humano + Pet">Humano + Pet</option>
              </select>
            </div>
          </div>

          {/* Linha 2: Telefone, Celular, Vendedor (3 colunas) */}
          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
            <div className="s2-field">
              <label>TELEFONE</label>
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange} />
            </div>
            <div className="s2-field">
              <label>CELULAR</label>
              <input type="tel" name="mobilePhone" value={formData.mobilePhone} onChange={handleChange} />
            </div>
            <div className="s2-field">
              <label>VENDEDOR</label>
              <input type="text" name="vendedor" value={formData.vendedor} onChange={handleChange} placeholder="Nome do vendedor" />
            </div>
          </div>

          <div className="cpfl-divider" />

          {/* Endereço (opcional) - usando grid de 4 colunas */}
          <div className="step3-inline-title" style={{ marginBottom: 16 }}>
            <CreditCard size={16} /> <strong>Endereço (opcional)</strong>
          </div>

          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '2fr 0.8fr 1.2fr', gap: '14px' }}>
            <div className="s2-field">
              <label>LOGRADOURO</label>
              <input type="text" name="address" value={formData.address} onChange={handleChange} />
            </div>
            <div className="s2-field">
              <label>NÚMERO</label>
              <input type="text" name="addressNumber" value={formData.addressNumber} onChange={handleChange} />
            </div>
            <div className="s2-field">
              <label>COMPLEMENTO</label>
              <input type="text" name="complement" value={formData.complement} onChange={handleChange} />
            </div>
          </div>

          <div className="t3-row" style={{ marginBottom: 16, display: 'grid', gridTemplateColumns: '1fr 1fr 0.7fr 0.8fr', gap: '14px' }}>
            <div className="s2-field">
              <label>BAIRRO</label>
              <input type="text" name="province" value={formData.province} onChange={handleChange} />
            </div>
            <div className="s2-field">
              <label>CIDADE</label>
              <input type="text" name="city" value={formData.city} onChange={handleChange} />
            </div>
            <div className="s2-field">
              <label>UF</label>
              <input type="text" name="state" value={formData.state} onChange={handleChange} maxLength="2" />
            </div>
            <div className="s2-field">
              <label>CEP</label>
              <input type="text" name="postalCode" value={formData.postalCode} onChange={handleChange} />
            </div>
          </div>

          <div className="cpfl-divider" />

          {/* Dependentes */}
          <div className="step3-section">
            <div className="step3-section-title"><Users size={18} /> Dependentes</div>
            <div className="dep-add-row">
              <div className="s2-field" style={{ flex: 1, minWidth: 220 }}>
                <label>NOME COMPLETO</label>
                <input type="text" value={depForm.nome} onChange={e => setD('nome', e.target.value)} placeholder="Nome completo do dependente" />
              </div>
              <div className="s2-field" style={{ width: 160, flexShrink: 0 }}>
                <label>DATA DE NASCIMENTO</label>
                <input type="date" value={depForm.dataNascimento} onChange={e => setD('dataNascimento', e.target.value)} />
              </div>
              <div className="s2-field" style={{ width: 180, flexShrink: 0 }}>
                <label>GRAU DE PARENTESCO</label>
                <select value={depForm.grau} onChange={e => setD('grau', e.target.value)}>
                  <option value="filho">Filho(a)</option>
                  <option value="conjuge">Cônjuge</option>
                  <option value="pai">Pai / Mãe</option>
                  <option value="agregado">Agregado</option>
                </select>
              </div>
              <button className="btn-add-dep" onClick={addDependente}><Plus size={16} /> Adicionar</button>
            </div>
            {dependentes.length > 0 && (
              <div className="dep-table-wrapper">
                <table className="dep-table">
                  <thead>
                    <tr><th>#</th><th>Nome</th><th>Nascimento</th><th>Grau</th><th>Opções</th></tr>
                  </thead>
                  <tbody>
                    {dependentes.map((d, i) => (
                      <tr key={d.id} className={i % 2 === 0 ? 'row-even' : 'row-odd'}>
                        <td className="dep-num">{i + 1}</td>
                        <td className="dep-nome">
                          <div className="td-name-inner">
                            <div className="mini-avatar dep-avatar">{d.nome.charAt(0)}</div>
                            {d.nome.toUpperCase()}
                          </div>
                        </td>
                        <td className="dep-nasc">
                          {calcIdade(d.dataNascimento)}{' '}
                          {d.dataNascimento ? new Date(d.dataNascimento + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                        </td>
                        <td><span className={`grau-badge grau-${d.grau}`}>{grauLabels[d.grau]}</span></td>
                        <td>
                          <button className="btn-dep-action excluir" onClick={() => removeDep(d.id)}>
                            <Trash2 size={13} /> Excluir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="cpfl-divider" />

          {/* Anexar Documentos (no final) */}
          <div className="step3-inline-title" style={{ marginBottom: 16 }}>
            <CloudUpload size={16} /> <strong>Anexar Documentos</strong>
          </div>
          <div style={{ marginBottom: 24 }}>
            <button
              type="button"
              className="btn-anexar"
              onClick={() => document.getElementById('docs-input').click()}
            >
              <CloudUpload size={16} /> Selecionar arquivos
            </button>
            <input
              id="docs-input"
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
            {docs.length > 0 && (
              <ul className="docs-list">
                {docs.map((doc, idx) => (
                  <li key={idx} className="doc-item">
                    <span className="doc-name">{doc.name}</span>
                    <button type="button" className="btn-doc-remove" onClick={() => removeDoc(idx)}>
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <div style={{ color: '#e11d48', marginTop: 12, fontSize: 13 }}>❌ {error}</div>}

          <div className="cpfl-actions">
            <button type="button" className="btn-cancel-edit" onClick={onBack}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-save-only"
              onClick={handleSave}
              disabled={loading}
            >
              💾 Salvar Cadastro
            </button>
            <button
              type="button"
              className="btn-save-charge"
              onClick={handleSaveAndCharge}
              disabled={loading}
            >
              ⚡ Salvar e Gerar Cobrança
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}